import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, getPriceTypeLabel, materialStateEnum, priceTypeEnum, stepModeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyRentalMutation, useDailyRentalMutation } from '../../../../hooks/mutations/useDailyRentalMutation';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailyRental } from '../../../../services/apiSLTP/dailyReports/dailyRentals';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailyRentalModalProps extends ModalProps {
    activity: DataItem;
    refetch: () => void;
    dailyRental: DailyRental | null;
    createTransfer: boolean;
}

const ManageDailyRentalModal: React.FC<ManageDailyRentalModalProps> = ({ open, handleClose, activity, refetch, dailyRental, createTransfer }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentRentalId, setCurrentRentalId] = useState(dailyRental?._id);
    const [selectedRentalType, setSelectedRentalType] = useState({ label: '', value: '' });
    const [rentalDesignation, setRentalDesignation] = useState('');
    const [observations, setObservations] = useState('');
    const [isCreateTransfer, setIsCreateTransfer] = useState(createTransfer);
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({ label: capitalizeFirstLetter(t('present')), value: materialStateEnum.PRESENT.toString() });
    const [price, setPrice] = useState(typeof dailyRental?.price === 'number' ? dailyRental?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyRental?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(dailyRental?.priceType))),
                  value: (dailyRental?.priceType).toString(),
              }
            : null
    );

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];

    const {
        data: dataRentals,
        isLoading: isLoadingRentals,
        isError: isErrorRentals,
        isSuccess: isSuccessRentals,
        refetch: refetchRentals,
    } = useRentalsQuery({ queries: { active: true } });

    const { mutate: updateDailyRental } = useDailyRentalMutation({ onSuccess: refetch });
    const { mutate: createDailyRental } = useCreateDailyRentalMutation({ onSuccess: refetch });

    const postDailyRental = () => {
        createDailyRental({
            body: {
                activityId: activity._id,
                rentalTypeId: selectedRentalType.value,
                designation: rentalDesignation.trim(),
                state: parseInt(selectedState.value),
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : undefined,
                priceType: priceType?.value ? parseInt(priceType.value) : undefined,
                observations,
            },
        });
    };

    const patchDailyRental = () => {
        if (!currentRentalId) return;
        updateDailyRental({
            id: currentRentalId,
            body: {
                rentalTypeId: selectedRentalType.value,
                state: parseInt(selectedState.value),
                designation: rentalDesignation,
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : null,
                priceType: priceType?.value ? parseInt(priceType.value) : null,
                observations,
            },
            queries: {
                syncTransfers: true,
            },
        });
    };

    useEffect(() => {
        if (dailyRental) {
            setSelectedRentalType({ label: dailyRental.rentalTypeId?.designation || '', value: dailyRental.rentalTypeId?._id || '' });
            setRentalDesignation(dailyRental.designation);
            setSelectedState({
                label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyRental.state))) || '',
                value: dailyRental.state.toString() || '',
            });
            setObservations(dailyRental.observations || '');
        }
    }, []);

    return (
        <ModalBody
            title={t('rental')}
            description={activity.siteId.name}
            subtitle={!!currentRentalId?.length ? t('update') : t('add')}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentRentalId?.length ? postDailyRental() : patchDailyRental();
            }}
            disableSubmit={!(selectedRentalType.value.length && rentalDesignation.length && selectedState.value.length)}
        >
            <DropDown
                selectedItem={selectedRentalType}
                items={dataRentals?.map((value) => ({ label: value.designation, value: value._id })) || []}
                handleChange={(event, value) => setSelectedRentalType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
                loading={isLoadingRentals}
            />
            <CustomTextInput value={rentalDesignation} onChange={(text) => setRentalDesignation(text)} label={t('name')} required />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            <CustomTextInput value={observations} onChange={(text) => setObservations(text)} label={t('observations')} multiline />

            {activity.stepMode !== stepModeEnum.VALIDATE && (
                <ToggleLine
                    checked={isCreateTransfer}
                    onChange={(checked) => setIsCreateTransfer(checked)}
                    title={capitalizeFirstLetter(t('createTransfer'))}
                    disabled={parseInt(selectedState.value) === materialStateEnum.PRESENT}
                />
            )}
            {activity.stepMode === stepModeEnum.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyRentalModal;
